import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Grid, Typography, useTheme } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ForumIcon from '@mui/icons-material/Forum';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import WebAssetIcon from '@mui/icons-material/WebAsset';
import { COLORS } from '../theme/colors';
import PageLayout from '../components/layout/PageLayout';

type BenefitCardText = {
  id: BenefitCardId;
  title: string;
  description: string;
};

type BenefitCardId = 'automatizar' | 'ia' | 'centralizar' | 'integrar' | 'desarrollar';

type BenefitCardLayout = {
  id: BenefitCardId;
  icon: ReactNode;
  md: 4 | 8;
};

const BENEFIT_LAYOUT: BenefitCardLayout[] = [
  {
    id: 'automatizar',
    icon: <AutoFixHighIcon />,
    md: 4,
  },
  {
    id: 'ia',
    icon: <SmartToyIcon />,
    md: 8,
  },
  {
    id: 'centralizar',
    icon: <ForumIcon />,
    md: 4,
  },
  {
    id: 'integrar',
    icon: <EventAvailableIcon />,
    md: 4,
  },
  {
    id: 'desarrollar',
    icon: <WebAssetIcon />,
    md: 4,
  },
];

function BenefitCardItem({
  card,
  icon,
  md,
}: {
  card: BenefitCardText;
  icon: ReactNode;
  md: 4 | 8;
}): JSX.Element {
  return (
    <Box
      sx={{
        height: '100%',
        minHeight: { xs: 210, md: 220 },
        p: { xs: 3, md: 3.25 },
        borderRadius: '10px',
        position: 'relative',
        overflow: 'hidden',
        background:
          'linear-gradient(145deg, rgba(30,27,46,0.92) 0%, rgba(24,22,41,0.96) 100%)',
        border: '1px solid rgba(168,85,247,0.08)',
        boxShadow: '0 18px 60px rgba(0,0,0,0.18)',
        transition: 'all 0.28s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 82% 18%, rgba(168,85,247,0.13) 0%, transparent 38%)',
          opacity: 0.9,
          pointerEvents: 'none',
        },
        '&:hover': {
          transform: 'translateY(-5px)',
          borderColor: 'rgba(168,85,247,0.34)',
          boxShadow: '0 24px 80px rgba(147,51,234,0.18)',
        },
        '&:hover .benefit-icon': {
          transform: 'translateY(-2px) scale(1.04)',
          boxShadow:
            '0 0 24px rgba(191,0,255,0.48), 0 0 46px rgba(168,85,247,0.22)',
        },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          className="benefit-icon"
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            color: '#fff',
            background:
              'radial-gradient(circle at 30% 25%, #d946ef 0%, #9333ea 48%, #6b21a8 100%)',
            boxShadow:
              '0 0 22px rgba(191,0,255,0.42), 0 0 42px rgba(168,85,247,0.18)',
            transition: 'all 0.28s ease',
            '& svg': {
              fontSize: 24,
            },
          }}
        >
          {icon}
        </Box>

        <Typography
          variant="h3"
          sx={{
            color: '#ffffff',
            fontSize: { xs: '1.15rem', md: '1.22rem' },
            fontWeight: 700,
            mb: 1.3,
            lineHeight: 1.3,
          }}
        >
          {card.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: COLORS.gray[400],
            lineHeight: 1.7,
            maxWidth: md === 8 ? 520 : 330,
            fontSize: { xs: '0.9rem', md: '0.92rem' },
          }}
        >
          {card.description}
        </Typography>
      </Box>
    </Box>
  );
}

export default function QueGanasPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const cards = t('queGanas.cards', { returnObjects: true }) as BenefitCardText[];

  return (
    <PageLayout>
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          background: isDark
            ? `
              radial-gradient(circle at 50% 0%, rgba(168,85,247,0.22) 0%, rgba(147,51,234,0.11) 24%, transparent 48%),
              radial-gradient(circle at 14% 38%, rgba(147,51,234,0.11) 0%, transparent 34%),
              radial-gradient(circle at 86% 48%, rgba(168,85,247,0.13) 0%, transparent 36%),
              linear-gradient(180deg, ${COLORS.dark[900]} 0%, ${COLORS.dark[800]} 42%, ${COLORS.dark[900]} 100%)
            `
            : `
              radial-gradient(circle at 50% 0%, rgba(168,85,247,0.16) 0%, rgba(147,51,234,0.07) 24%, transparent 48%),
              linear-gradient(180deg, ${COLORS.dark[900]} 0%, ${COLORS.dark[800]} 42%, ${COLORS.dark[900]} 100%)
            `,
          pt: { xs: 10, md: 12 },
          pb: { xs: 9, md: 13 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: { xs: -160, md: -230 },
            left: '50%',
            transform: 'translateX(-50%)',
            width: { xs: 520, md: 900 },
            height: { xs: 520, md: 900 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.26) 0%, rgba(147,51,234,0.13) 32%, transparent 68%)',
            filter: 'blur(58px)',
            opacity: 0.7,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: '44%',
            right: { xs: -220, md: -120 },
            width: { xs: 360, md: 620 },
            height: { xs: 360, md: 620 },
            borderRadius: '999px',
            background:
              'radial-gradient(circle, rgba(168,85,247,0.18) 0%, rgba(147,51,234,0.08) 38%, transparent 72%)',
            filter: 'blur(64px)',
            opacity: 0.62,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              textAlign: 'center',
              maxWidth: 760,
              mx: 'auto',
              mb: { xs: 6, md: 8 },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: '#ffffff',
                fontSize: {
                  xs: '2rem',
                  sm: '2.45rem',
                  md: '3.15rem',
                  lg: '3.45rem',
                },
                fontWeight: 700,
                lineHeight: {
                  xs: 1.16,
                  md: 1.12,
                },
                mb: 2.2,
              }}
            >
              {t('queGanas.hero.title')}{' '}
              <Box
                component="span"
                sx={{
                  display: 'block',
                  background: COLORS.gradients.brandLight,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  textShadow: '0 0 28px rgba(216,180,254,0.36)',
                }}
              >
                {t('queGanas.hero.highlight')}
              </Box>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: COLORS.gray[400],
                maxWidth: 620,
                mx: 'auto',
                lineHeight: 1.7,
                fontSize: { xs: '1rem', md: '1.15rem' },
              }}
            >
              {t('queGanas.hero.subtext')}
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2.5, md: 3 }}>
            {BENEFIT_LAYOUT.map((layout) => {
              const card = cards.find((item) => item.id === layout.id);

              if (!card) return null;

              return (
                <Grid item xs={12} md={layout.md} key={layout.id}>
                  <BenefitCardItem card={card} icon={layout.icon} md={layout.md} />
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    </PageLayout>
  );
}